import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Select,
  styled,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Typography,
  Tooltip,
  RadioGroup,
  Radio,
  FormControlLabel,
} from "@mui/material";
import { IWithClassName } from "../../../theme/default.theme";
import StyledTable from "../../../component/table";
import {
  useAppSelector,
  useAppDispatch,
  IRootState,
} from "../../../redux/store";
import { ITableColumn } from "../../../component/table/component";
import { ICampaignFiltersDto } from "../../../interfaces/dto/campaign.dto";
import { SearchOutlined } from "@mui/icons-material";
import { CampaignStatus } from "../../../enum/campaign";
import MenuAction from "./menu-action";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { createSelector } from "@reduxjs/toolkit";
import { Role } from "../../../enum/auth";
import TrackierModal from "./trackier-modal";
import Debounce from "../../../hooks/use-debounce";
import { Link } from "react-router-dom";

const campaignStatusOptions = [
  {
    id: CampaignStatus.All,
    title: "All",
  },
  {
    id: CampaignStatus.Active,
    title: "Active",
  },
  {
    id: CampaignStatus.Completed,
    title: "Completed",
  },
  {
    id: CampaignStatus.Deleted,
    title: "Deleted",
  },
  {
    id: CampaignStatus.Inactive,
    title: "Inactive",
  },
  {
    id: CampaignStatus.Pending,
    title: "Pending",
  },
];

const campignStatusButton = [
  CampaignStatus.Inactive,
  CampaignStatus.Pending,
  CampaignStatus.Active,
];

const selector = createSelector(
  (state: IRootState) => state.campaign.list,
  (state: IRootState) => state.campaign.trackier.list,
  (state: IRootState) => state.auth,
  (campaign, trackier, auth) => ({
    campaign,
    auth,
    trackier,
  }),
);

interface IProps extends IWithClassName {
  fetchCampaigns: (campaignListfilters: ICampaignFiltersDto) => void;
  onStatusChange: (id: string, status: CampaignStatus, campaignListfilters: ICampaignFiltersDto) => void;
}

const CampaignTable = ({ className, fetchCampaigns, onStatusChange }: IProps) => {
  const dispatch = useAppDispatch();
  const { campaign, auth, trackier } = useAppSelector(selector);
  const { isLoading, pagination, filters, data } = campaign;
  const { role } = auth;

  const [campaignListfilters, setCampaignListFilters] =
    useState<ICampaignFiltersDto>({
      offset: 0,
      limit: 10,
    });
  const [page, setPage] = useState(1);
  const [showTrackierModal, setTrackierModal] = useState<boolean>(false);
  const [campaignId, setCampaignId] = useState<string>("");
  const [search, setSearch] = useState<string>("");

  const debouceSearch = Debounce(search);

  const columns =  useMemo(() => [
    {
      id: "name",
      title: "company Name",
      sortable: true,
    },
    ...(role === Role.ADMIN ? [{
      id: "userName",
      title: "Created By",
      sortable: true,
    }] : []),
    {
      id: "status",
      title: "Status",
      sortable: false,
    },
    {
      id: "type",
      title: "Type",
      sortable: true,
    },
    {
      id: "bid",
      title: "BID",
      sortable: true,
    },
    {
      id: "clicks",
      title: "Clicks",
      sortable: true,
    },
    {
      id: "installs",
      title: "Installs",
      sortable: true,
    },
    {
      id: "totalSpend",
      title: "Total Spend",
      sortable: true,
    },
    {
      id: "actions",
      title: "Actions",
      sortable: true,
    },
  ], [role]);
  
  useEffect(() => {
    setCampaignListFilters((filters) => ({
      ...filters,
      search: debouceSearch ?? "",
    }));
  }, [debouceSearch]);

  useEffect(() => {
    fetchCampaigns(campaignListfilters);
  }, [dispatch, campaignListfilters, fetchCampaigns]);

  const openTrackierModal = (isOpen: boolean, campaignId: string) => {
    setTrackierModal(isOpen);

    setCampaignId(campaignId);
  }

  const renderCell = (
    rowIndex: number,
    columnIndex: number,
    column: ITableColumn,
  ) => {
    try {
      const offset = (page - 1) * (campaignListfilters.limit ?? 10);
      switch (column.id) {
        case "userName":
          const user = data?.[rowIndex - offset].userId ?? "";
          return typeof user === "string" ? user : user.name;
        case "name":
          return (
            <Grid className="app-info">
              
                <Grid item>
                  <Link to={`/campaign/${data?.[rowIndex - offset].id}/0`}>
                    <img
                      src={data?.[rowIndex - offset].app?.thumbnail}
                      alt={data?.[rowIndex - offset].app?.name}
                    />
                  </Link>
                </Grid>
                
                <Grid item>
                  <Link to={`/campaign/${data?.[rowIndex - offset].id}/0`}>
                    {data?.[rowIndex - offset].app?.name}
                  </Link>
                </Grid>
              
            </Grid>
          );
        case "installs":
          return (
            <Typography>
              {data?.[rowIndex - offset].installs?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            </Typography>
          );
        case "clicks":
          return (
            <Typography>
              {data?.[rowIndex - offset].clicks?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            </Typography>
          );
        case "totalSpend":
          return (
            <Typography>
              ${data?.[rowIndex - offset].spend?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            </Typography>
          );
        case "bid":
          return (
            <Typography>
              ${data?.[rowIndex - offset].bidding.bidAmount?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            </Typography>
          );
        case "actions":
          return <MenuAction campaign={data?.[rowIndex - offset].id ?? ""} openTrackierModal={openTrackierModal} />;
        case "status":
          return role === Role.ADMIN &&
            campignStatusButton.includes(
              data?.[rowIndex - offset].status as CampaignStatus,
            ) ? (
            <RadioGroup
              row
              className="status-radio-group"
              value={data?.[rowIndex - offset].status ?? ""}
              onChange={({ target }) =>
                onStatusChange(
                  data?.[rowIndex - offset].id ?? "",
                  target.value as CampaignStatus,
                  campaignListfilters,
                )
              }
            >
              {campignStatusButton.map((item) => (
                <FormControlLabel
                  value={item}
                  key={item}
                  className="status-radio"
                  control={
                    <Radio
                      key={item}
                      color="default"
                      value={item}
                      sx={{
                        px: 2,
                        alignItems: "center",
                      }}
                    />
                  }
                  label={item}
                />
              ))}
            </RadioGroup>
          ) : (
            <StatusLabel status={data?.[rowIndex - offset].status ?? ""} />
          );
        default:
          return rowIndex >= offset
            ? data?.[rowIndex - offset][column.id as keyof (typeof data)[0]]
            : "";
      }
    } catch (e) {
      console.log(e);
      return "";
    }
  };

  const onPageChange = (page: number) => {
    setPage(page);
    setCampaignListFilters((state) => ({
      ...state,
      offset: (page - 1) * (campaignListfilters.limit ?? 10),
    }));
  };

  const onColumnOrder = (
    columnIndex: number,
    column: ITableColumn,
    direction: "asc" | "desc",
  ) => {
    setCampaignListFilters((state) => ({
      ...state,
      sortBy: column.id,
      sortDirection: direction,
    }));
  };

  return (
    <Box className={className}>
      <TrackierModal 
        open={showTrackierModal}
        handleClose={() => setTrackierModal(false)}
        trackierIds={trackier.data ?? []}
        campaignId={campaignId} />

      <StyledTable
        columns={columns}
        currentPage={pagination?.page ?? 1}
        renderCell={renderCell}
        rowsPerPage={filters?.limit ?? 10}
        totalRows={pagination?.total ?? 1}
        uniqueRowId={(rowIndex) => `${rowIndex}`}
        customHeader="Overview"
        onPageChange={onPageChange}
        onColumnOrder={onColumnOrder}
        isLoading={isLoading}
        onRowsPerPageChange={(rows) => {
          setCampaignListFilters((state) => ({
            ...state,
            limit: rows,
          }));
        }}
      >
        <Box className="filter-box">
          <TextField
            className="search-input"
            label={<SearchOutlined />}
            value={search}
            onChange={({ target }) => setSearch(target.value)}
          />
          <FormControl>
            <InputLabel htmlFor="country-selector">Status</InputLabel>
            <Select
              label="Status"
              placeholder="Status"
              value={campaignListfilters.status ?? ""}
              className="status-select"
              onChange={({ target }) => {
                setCampaignListFilters((state) => ({
                  ...state,
                  status: target.value,
                }));
              }}
            >
              {campaignStatusOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </StyledTable>
    </Box>
  );
};

const StatusLabel = ({ status }: { status: string }) => {
  const getStatusClass = () => {
    switch (status) {
      case CampaignStatus.Active:
        return "status-tag active";
      case CampaignStatus.Completed:
        return "status-tag success";
      case CampaignStatus.Deleted:
        return "status-tag error";
      case CampaignStatus.Inactive:
        return "status-tag error";
      case CampaignStatus.Pending:
        return "status-tag warning";
      default:
        return "status-tag";
    }
  };

  return (
    <Box className="status-cont">
      <Box component="span" className={getStatusClass()}>
        <Typography>{status}</Typography>
      </Box>
      <Tooltip title="Campaign status">
        <InfoOutlinedIcon className="info-icon" />
      </Tooltip>
    </Box>
  );
};

export default styled(CampaignTable)(({ theme }) => ({
  ".status-radio-group": {
    display: "inline-block",
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 5,
  },
  ".status-radio": {
    padding: theme.spacing(0.5, 1),
    textTransform: "capitalize",
    border: "none",
    ".MuiRadio-root": {
      display: "none",
    },
  },
  ".status-cont": {
    display: "flex",
  },
  ".status-tag": {
    display: "inline-flex",
    padding: theme.spacing(0, 0.5),
    borderRadius: 5,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textTransform: "capitalize",
    textAlign: "center",
    minWidth: 80,
    p: {
      fontSize: 11,
      fontWeight: 600,
      margin: "auto",
    },
  },
  ".info-icon": {
    margin: theme.spacing(0, 0.5),
    color: theme.palette.text.secondary,
  },
  ".success": {
    backgroundColor: theme.palette.success.main,
  },
  ".warning": {
    backgroundColor: theme.palette.warning.main,
  },
  ".error": {
    backgroundColor: theme.palette.error.main,
  },
  ".active": {
    backgroundColor: theme.palette.success.main,
  },
  ".status-select": {
    width: 150,
  },
  ".filter-box": {
    display: "flex",
    gap: 10,
  },
  ".app-info": {
    display: "flex",
    alignItems: "center",
    gap: 5,
    "& a": {
      textDecoration: "none",
      color: theme.palette.text.primary,
    },
    "& img": {
      width: 50,
      height: 50,
      objectFit: "contain",
    },
  },
}));
